import { SEARCH_PARAM_KEYS } from '@/constants/auth'
import { GetProfileMeResponse } from '@/services/auth'
import { HubsStoreState, ViveportAuth } from '@/types/store'

const { HTC_TOKEN, RETICULUM_TOKEN, EMAIL, RETRY_AFTER } = SEARCH_PARAM_KEYS

export const createCredentials = (qs: URLSearchParams) => {
  const retryAfter = parseInt(qs.get(RETRY_AFTER)!, 0)
  const expiredAt = Date.now() + retryAfter * 1000
  return {
    token: qs.get(RETICULUM_TOKEN) || '',
    email: qs.get(EMAIL) || '',
    htcToken: qs.get(HTC_TOKEN) || '',
    expiredAt: expiredAt,
  }
}

export const createProfile = (result: Omit<GetProfileMeResponse, 'htcAccountId'>) => ({
  id: result.id,
  displayName: result.displayName,
  isOver18: result.isOver18,
  userId: result.userId,
  avatarId: result.activeAvatar.id,
  avatarSnapshot: result.activeAvatar.snapshot,
  avatarGlb: result.activeAvatar.glbUrl,
  isHalfBody: result.activeAvatar.isHalfBody,
  avatarGenderKey: result.activeAvatar.gender,
  avatarDataType: result.activeAvatar.dataType,
  bio: result.bio,
  image: result.profileImage,
  banner: result.profileBanner,
  bannerMeta: result.profileBannerMeta,
  medias: result.profileMedia,
  showSocialLinks: result.showSocialLinks,
  socialLinks: result.socialLinks,
  showFollowingCreators: result.showFollowingCreators,
  pauseWatchHistory: result.pauseWatchHistory,
  isGamePlus: result.isGamePlus,
})

export const createShareComponentProfile = (
  profile: HubsStoreState['profile'],
  points?: number,
) => ({
  displayName: profile?.displayName,
  activeAvatar: {
    id: profile?.avatarId,
    snapshot_url: profile?.avatarSnapshot,
    avatar_url: profile?.avatarGlb,
    head_icon_url: profile?.avatarSnapshot,
    gender: profile?.avatarGenderKey,
    data_type: profile?.avatarDataType,
    is_half_body: false,
  },
  userID: profile?.userId,
  points,
  token: profile?.htcToken,
  accountId: profile?.accountId,
  isViversePlus: profile?.isGamePlus,
})

export const createViveportAuth = (
  result: Pick<GetProfileMeResponse, 'walletAddress' | 'accountEmail' | 'accountPhoneNumber'>,
) => ({
  walletAddress: result.walletAddress,
  accountEmail: result.accountEmail,
  accountPhoneNumber: result.accountPhoneNumber,
})

export const removeKeysFromSearchParams = (keys: string[]) => {
  const searchParams = new URLSearchParams(location.search)
  keys.forEach((key) => searchParams.delete(key))
  const searchParamString = searchParams.toString()
  const hash = location.hash
  const newURL = `${location.origin}${location.pathname}${searchParamString && '?' + searchParamString}${hash}`
  window.history.replaceState(null, '', newURL)
}

export const extractUsernameFromEmail = (email: string) => {
  if (!email) return ''
  const atIndex = email.indexOf('@')
  return email.slice(0, atIndex)
}

export const deriveAccountType = ({
  walletAddress,
  accountEmail,
  accountPhoneNumber,
}: Partial<ViveportAuth>): 'wallet_account' | 'htc_account' | 'guest' => {
  if (walletAddress) return 'wallet_account'
  if (accountEmail || accountPhoneNumber) return 'htc_account'
  return 'guest'
}

type HtcAccessCookiePayload = {
  access_token?: unknown
  account_id?: unknown
}

const parseHtcAccessCookie = (htcSSO?: string, isSSR: boolean = false): HtcAccessCookiePayload => {
  if (!htcSSO) return {}

  try {
    const atobFn = isSSR ? globalThis.atob : atob
    const decoded = atobFn(htcSSO)
    const payload: unknown = JSON.parse(decoded)
    return typeof payload === 'object' && payload !== null ? payload : {}
  } catch (error) {
    console.error('Error parsing SSO cookie', error)
    return {}
  }
}

export const getHtcAuthKey = async (htcSSO?: string, isSSR: boolean = false): Promise<string> => {
  const { access_token: token = '' } = parseHtcAccessCookie(htcSSO, isSSR)
  return typeof token === 'string' ? token : ''
}

export const getHtcAccountId = (htcSSO?: string): string => {
  const { account_id: accountId = '' } = parseHtcAccessCookie(htcSSO)
  return typeof accountId === 'string' ? accountId : ''
}
